/**
 * Client Tracker Content — Item #1 (Iter 138)
 *
 * Design matched to Product Installation page.
 * Adds: product field (VIP / Free Trial / Indicator / Smart Desk / Alert / Others),
 *       expiry_date + expiry alerts (1d/3d/7d), status "expired",
 *       autofill on name search, pipeline chart (status + product breakdown),
 *       CSV import + preview seed.
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Users, UserPlus, UserX, TrendingUp, AlertTriangle, Search, Plus, Trash2, Edit3,
  Phone, Mail, DollarSign, Loader2, Bell, Package, Upload, Download, ChevronDown,
  Filter, Clock, Zap, Sparkles, FileSpreadsheet, X, ShieldAlert, RefreshCcw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as ReTooltip, Legend } from 'recharts';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { SALES_ADMINS } from '../constants/salesAdmins';
import ClientFollowUpBoard from './ClientFollowUpBoard';
import ClientPriorityList from './ClientPriorityList';
import CommissionBoardModal from './CommissionBoardModal';
import { createPortal } from 'react-dom';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { usePersistedState, useStableKeyId } from '@/hooks/usePersistedState';

const API = process.env.REACT_APP_BACKEND_URL;

// Dev/preview-only tools shown only when NOT on the production host.
const IS_PREVIEW = typeof window !== 'undefined' && !window.location.hostname.includes('fund-tracking-2.emergent.host');

const STATUS_META = {
  active:         { label: 'Active',        color: '#10B981', bg: 'bg-[#10B981]/15 text-[#10B981] border-[#10B981]/30' },
  expiring_soon:  { label: 'Expiring Soon', color: '#EAB308', bg: 'bg-[#EAB308]/15 text-[#EAB308] border-[#EAB308]/30' },
  expired:        { label: 'Expired',       color: '#F59E0B', bg: 'bg-[#F59E0B]/15 text-[#F59E0B] border-[#F59E0B]/30' },
};

// Access status is derived from the expiry date — never keyed in by hand.
const accessState = (expiry, todayISO) => {
  const exp = (expiry || '').trim();
  if (!exp) return '';
  if (exp < todayISO) return 'expired';
  const soon = new Date(todayISO);
  soon.setDate(soon.getDate() + 7);
  return exp <= soon.toISOString().slice(0, 10) ? 'expiring_soon' : 'active';
};

const CLIENT_TYPE_META = {
  new:      { label: 'New',      color: '#3B82F6', bg: 'bg-[#3B82F6]/15 text-[#3B82F6] border-[#3B82F6]/30' },
  existing: { label: 'Existing', color: '#8B5CF6', bg: 'bg-[#8B5CF6]/15 text-[#8B5CF6] border-[#8B5CF6]/30' },
};

const TIER_META = {
  0: { label: '—',      color: '#6B7280' },
  1: { label: 'Tier 1', color: '#3B82F6' },
  2: { label: 'Tier 2', color: '#F59E0B' },
  3: { label: 'Tier 3', color: '#EF4444' },
  4: { label: 'Done',   color: '#10B981' },
  5: { label: 'Not Interested', color: '#94A3B8' },
};

// Days since the last follow-up before the client is due again, by current tier
const TIER_DUE_DAYS = { 0: 0, 1: 1, 2: 3, 3: 7 };
const CLOSED_TIERS = [4, 5];

const PRODUCT_META = {
  'VIP':         { color: '#8B5CF6', icon: Sparkles },
  'Free Trial':  { color: '#3B82F6', icon: Clock },
  'Indicator':   { color: '#10B981', icon: TrendingUp },
  'Smart Desk':  { color: '#F59E0B', icon: Package },
  'Alert':       { color: '#EF4444', icon: Bell },
  'Others':      { color: '#6B7280', icon: Users },
};

const fmtMYR = (n) => `RM${Number(n || 0).toLocaleString('en-MY', { maximumFractionDigits: 0 })}`;
const emptyForm = {
  name: '', email: '', phone: '', broker: '', account_id: '',
  product: 'Others', status: 'active', client_type: 'new', follow_up_tier: 0, follow_up_by: '', renew_count: 1,
  closed_by: '',
  ltv_myr: 0, deposit_myr: 0,
  joined_date: '', expiry_date: '', last_contact_date: '', next_follow_up_date: '',
  remarks: '', notes: '',
};

// ─────────────────────────────────────────────────────────────
// Small reusable pieces matching the CRM design language
// ─────────────────────────────────────────────────────────────
function SectionCard({ children, className = '' }) {
  return (
    <div className={`rounded-xl bg-[hsl(var(--surface))] border border-[hsl(var(--border))] ${className}`}>
      {children}
    </div>
  );
}

function SectionHeader({ icon: Icon, title, subtitle, right }) {
  return (
    <div className="px-4 py-3 border-b border-[hsl(var(--border))] flex items-center justify-between gap-3 flex-wrap">
      <div className="flex items-center gap-2">
        <Icon size={14} className="text-[#E63946]" />
        <div>
          <p className="text-[12px] font-bold text-white">{title}</p>
          {subtitle && <p className="text-[10px] text-[hsl(var(--text-muted))] mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {right}
    </div>
  );
}

function ScoreCard({ label, value, icon: Icon, color, hint, testId }) {
  return (
    <div className="p-3.5 rounded-xl bg-[hsl(var(--surface))] border border-[hsl(var(--border))] hover:border-[hsl(var(--border-hover))] transition-all group" data-testid={testId}>
      <div className="flex items-center gap-2 mb-1.5">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center transition-all" style={{ background: `${color}15` }}>
          <Icon size={13} style={{ color }} />
        </div>
        <span className="text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--text-muted))]">{label}</span>
      </div>
      <p className="text-xl font-black font-mono" style={{ color }}>{value}</p>
      {hint && <p className="text-[9px] text-[hsl(var(--text-muted))] mt-0.5">{hint}</p>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Autocomplete search input
// ─────────────────────────────────────────────────────────────
function AutocompleteSearch({ ibId, value, onChange, onPick }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const boxRef = useRef(null);
  const listRef = useRef(null);
  const [pos, setPos] = useState(null);

  // Dropdown is portalled to <body> so the filter bar's horizontal scroll can't clip it
  useEffect(() => {
    if (!open) return;
    const measure = () => {
      if (!boxRef.current) return;
      const r = boxRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 260) });
    };
    measure();
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [open, suggestions.length]);

  useEffect(() => {
    if (!value || value.length < 2) { setSuggestions([]); return; }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API}/api/clients/${ibId}/autocomplete?q=${encodeURIComponent(value)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setSuggestions(data.suggestions || []);
          setOpen(true);
        }
      } catch { /* ignore */ }
    }, 200);
    return () => timer.current && clearTimeout(timer.current);
  }, [value, ibId]);

  useEffect(() => {
    const off = (e) => {
      const inBox = boxRef.current && boxRef.current.contains(e.target);
      const inList = listRef.current && listRef.current.contains(e.target);
      if (!inBox && !inList) setOpen(false);
    };
    document.addEventListener('mousedown', off);
    return () => document.removeEventListener('mousedown', off);
  }, []);

  return (
    <div className="relative flex-1 min-w-[180px] max-w-[280px]" ref={boxRef}>
      <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--text-muted))]" />
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => value.length >= 2 && setOpen(true)}
        placeholder="Search name, email, phone…"
        className="pl-9 h-8 text-xs bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]"
        data-testid="client-search"
      />
      {open && suggestions.length > 0 && pos && createPortal(
        <div
          ref={listRef}
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width }}
          className="z-[100] bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-lg shadow-2xl max-h-80 overflow-y-auto"
          data-testid="autofill-dropdown"
        >
          {suggestions.map((s) => {
            const meta = STATUS_META[s.status] || STATUS_META.expired;
            return (
              <button
                key={s.id}
                onClick={() => { onPick(s); setOpen(false); }}
                className="w-full text-left px-3 py-2 hover:bg-[hsl(var(--surface-overlay))] border-b border-[hsl(var(--border))]/40 flex items-center justify-between gap-2 transition-all"
                data-testid={`autofill-item-${s.id}`}
              >
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold text-white truncate">{s.name}</p>
                  {(s.email || s.phone) && (
                    <p className="text-[9px] text-[hsl(var(--text-muted))] truncate">
                      {s.email || s.phone}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {s.product && <span className="text-[9px] px-1.5 py-0.5 rounded border border-[hsl(var(--border))] text-[hsl(var(--text-muted))]">{s.product}</span>}
                  <span className={`text-[9px] px-1.5 py-0.5 rounded border ${meta.bg}`}>{meta.label}</span>
                </div>
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────
export default function ClientTrackerContent({ ib }) {
  const ibId = ib?.id;
  const [rows, setRows] = useState([]);
  const [agg, setAgg] = useState({
    total: 0, active: 0, expired: 0,
    total_ltv_myr: 0, avg_ltv_myr: 0, total_deposit_myr: 0, avg_deposit_myr: 0,
    churn_rate: 0, due_followups: 0, repeat_clients: 0, total_renewals: 0,
    by_client_type: { new: 0, existing: 0 },
    expiring_1d: 0, expiring_3d: 0, expiring_7d: 0,
    by_status: {}, by_product: {}, by_broker: {}, by_closed_by: {},
  });
  const [loading, setLoading] = useState(true);
  const P = `client-tracker:${useStableKeyId(ibId)}`;
  const [statusFilter, setStatusFilter] = usePersistedState(`${P}:status`, 'all');
  const [productFilter, setProductFilter] = usePersistedState(`${P}:product`, 'all');
  const [monthFilter, setMonthFilter] = usePersistedState(`${P}:month`, '__all__');
  const [search, setSearch] = usePersistedState(`${P}:search`, '');
  const [followUpFilter, setFollowUpFilter] = usePersistedState(`${P}:followup`, 'all');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const importInputRef = useRef(null);
  const [currentPage, setCurrentPage] = usePersistedState(`${P}:page`, 1);
  const [pageSize, setPageSize] = usePersistedState(`${P}:pageSize`, 15);
  const [showCommission, setShowCommission] = useState(false);
  const [typeFilter, setTypeFilter] = usePersistedState(`${P}:type`, 'all');
  const [dateFrom, setDateFrom] = usePersistedState(`${P}:dateFrom`, '');
  const [dateTo, setDateTo] = usePersistedState(`${P}:dateTo`, '');
  const [detailRow, setDetailRow] = useState(null);
  const [view, setView] = usePersistedState(`${P}:view`, 'table'); // 'table' | 'board' | 'priority'
  const [renewal, setRenewal] = useState(null);
  const [expiringOnly, setExpiringOnly] = usePersistedState(`${P}:expiringOnly`, false);
  const [detailHistory, setDetailHistory] = useState([]);
  const [dateOpen, setDateOpen] = useState(false);

  // Whenever filters or search change, reset to first page
  const filterKey = `${statusFilter}|${productFilter}|${monthFilter}|${search}|${typeFilter}|${dateFrom}|${dateTo}|${followUpFilter}`;
  const firstFilterRun = useRef(true);
  useEffect(() => {
    if (firstFilterRun.current) { firstFilterRun.current = false; return; }
    setCurrentPage(1);
  }, [filterKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchList = useCallback(async () => {
    if (!ibId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (productFilter !== 'all') params.append('product', productFilter);
      if (monthFilter && monthFilter !== '__all__') params.append('month', monthFilter);
      if (typeFilter !== 'all') params.append('client_type', typeFilter);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (search) params.append('search', search);
      const res = await fetch(`${API}/api/clients/${ibId}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setRows(data.clients || []);
        setAgg((prev) => data.aggregates || prev);
      }
    } catch (err) {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  }, [ibId, statusFilter, productFilter, monthFilter, search, typeFilter, dateFrom, dateTo]);

  useEffect(() => { fetchList(); }, [fetchList]);

  // Renewal alerts (expired / expiring within 7 days) → banner
  const fetchRenewal = useCallback(async () => {
    if (!ibId) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/clients/${ibId}/renewal-alerts?days=7`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setRenewal(await res.json());
    } catch { /* banner is non-critical */ }
  }, [ibId]);

  useEffect(() => { fetchRenewal(); }, [fetchRenewal, rows.length]);

  const expiringIds = useMemo(() => {
    if (!renewal) return new Set();
    const b = renewal.buckets || {};
    return new Set([...(b.expired || []), ...(b.today || []), ...(b.in_3d || []), ...(b.in_7d || [])].map((c) => c.id));
  }, [renewal]);

  const visibleRows = useMemo(() => {
    let out = expiringOnly ? rows.filter((r) => expiringIds.has(r.id)) : rows;
    if (followUpFilter !== 'all') {
      const tier = Number(followUpFilter);
      out = out.filter((r) => Number(r.follow_up_tier || 0) === tier);
    }
    return out;
  }, [rows, expiringOnly, expiringIds, followUpFilter]);

  const openCreate = () => { setEditingId(null); setForm(emptyForm); setShowForm(true); };
  const openEdit = (row) => { setEditingId(row.id); setForm({ ...emptyForm, ...row }); setShowForm(true); };

  const submit = async () => {
    if (!form.name || !form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const url = editingId ? `${API}/api/clients/${ibId}/${editingId}` : `${API}/api/clients/${ibId}`;
      const method = editingId ? 'PUT' : 'POST';
      const body = { ...form, ltv_myr: Number(form.ltv_myr) || 0, deposit_myr: Number(form.deposit_myr) || 0 };
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error();
      toast.success(editingId ? 'Client updated' : 'Client added');
      setShowForm(false);
      fetchList();
    } catch {
      toast.error('Failed to save client');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete client "${row.name}"?`)) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/clients/${ibId}/${row.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      toast.success('Client deleted');
      fetchList();
    } catch { toast.error('Failed to delete client'); }
  };

  // Access status is derived from Date Expiry, so there is no manual switch.

  // Inline follow-up tier change
  const updateTierInline = async (row, tier) => {
    const next = Number(tier);
    if (next === (row.follow_up_tier || 0)) return;
    setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, follow_up_tier: next } : r)));
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/clients/${ibId}/${row.id}/follow-up-tier`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ follow_up_tier: next }),
      });
      if (!res.ok) throw new Error();
      toast.success(next === 0 ? 'Follow-up direset' : `Follow-up → Tier ${next}`, { duration: 1500 });
      fetchList();
    } catch {
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, follow_up_tier: row.follow_up_tier || 0 } : r)));
      toast.error('Gagal update follow-up');
    }
  };

  // Inline "follow up by" (sales admin) change
  const updateFollowUpBy = async (row, who) => {
    if ((row.follow_up_by || '') === who) return;
    setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, follow_up_by: who } : r)));
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/clients/${ibId}/${row.id}/follow-up-by`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ follow_up_by: who }),
      });
      if (!res.ok) throw new Error();
      toast.success(who ? `Follow up by → ${who}` : 'Follow up by dikosongkan', { duration: 1500 });
      fetchList();
    } catch {
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, follow_up_by: row.follow_up_by || '' } : r)));
      toast.error('Gagal update follow up by');
    }
  };

  const openDetails = async (row) => {    setDetailRow(row);
    setDetailHistory([]);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/clients/${ibId}/${row.id}/history`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setDetailHistory(data.history || []);
      }
    } catch { /* ignore */ }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      let rowsIn = [];
      if (file.name.toLowerCase().endsWith('.json')) {
        rowsIn = JSON.parse(text);
      } else {
        // Naive CSV parser — first line = headers
        const lines = text.split(/\r?\n/).filter(Boolean);
        const headers = lines[0].split(',').map((h) => h.trim().toLowerCase());
        rowsIn = lines.slice(1).map((line) => {
          const values = line.split(',').map((v) => v.trim());
          const obj = {};
          headers.forEach((h, i) => { obj[h] = values[i]; });
          return obj;
        });
      }
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/clients/${ibId}/bulk-import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ rows: rowsIn }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      toast.success(`Imported ${data.inserted} of ${rowsIn.length} clients (${data.skipped} skipped)`);
      setShowImport(false);
      fetchList();
    } catch (err) {
      toast.error('Import failed — check file format');
    }
    e.target.value = '';
  };

  const exportCSV = () => {
    const headers = ['name', 'email', 'phone', 'broker', 'account_id', 'product', 'client_type', 'status', 'follow_up_tier', 'follow_up_by', 'renew_count', 'closed_by', 'deposit_myr', 'joined_date', 'expiry_date', 'next_follow_up_date', 'remarks', 'notes', 'last_edited_by'];
    const csv = [
      headers.join(','),
      ...rows.map((r) => headers.map((h) => `"${String(r[h] || '').replace(/"/g, '""')}"`).join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${ib?.name || 'clients'}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const seedPreview = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/clients/${ibId}/seed-preview`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        toast.info(data.reason || 'Seed skipped');
      } else {
        toast.success(`Seeded ${data.inserted} sample clients`);
        fetchList();
      }
    } catch { toast.error('Seed failed'); }
  };

  const clearSeeds = async () => {
    if (!window.confirm('Remove all preview-seeded clients?')) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/clients/${ibId}/seed-preview`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      toast.success(`Removed ${data.deleted} preview clients`);
      fetchList();
    } catch { toast.error('Clear failed'); }
  };

  const todayISO = new Date().toISOString().slice(0, 10);
  const expiringSoon = useMemo(() => {
    const out = { d1: [], d3: [], d7: [] };
    rows.forEach((r) => {
      if (!r.expiry_date || r.status === 'expired') return;
      const daysLeft = Math.floor((new Date(r.expiry_date) - new Date(todayISO)) / (1000 * 60 * 60 * 24));
      if (daysLeft <= 1) out.d1.push({ ...r, daysLeft });
      else if (daysLeft <= 3) out.d3.push({ ...r, daysLeft });
      else if (daysLeft <= 7) out.d7.push({ ...r, daysLeft });
    });
    return out;
  }, [rows, todayISO]);

  const monthOptions = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => { const jd = r.joined_date || ''; if (jd) set.add(jd.slice(0, 7)); });
    return Array.from(set).sort().reverse();
  }, [rows]);

  const statusPieData = useMemo(
    () => Object.entries(agg.by_status || {}).filter(([, v]) => v > 0).map(([k, v]) => ({ name: STATUS_META[k]?.label || k, value: v, color: STATUS_META[k]?.color || '#6B7280', key: k })),
    [agg.by_status]
  );
  const productPieData = useMemo(
    () => Object.entries(agg.by_product || {}).filter(([, v]) => v > 0).map(([k, v]) => ({ name: k, value: v, color: PRODUCT_META[k]?.color || '#6B7280', key: k })),
    [agg.by_product]
  );

  return (
    <div className="space-y-5" data-testid="client-tracker-content">
      {/* Sticky Header — matches Product Installation */}
      <header className="md:sticky md:top-0 z-20 bg-[#09090B]/95 backdrop-blur-md border-b border-[hsl(var(--border))]">
        <div className="px-4 sm:px-8 py-3 sm:py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            <h1 className="text-lg sm:text-2xl font-bold font-heading tracking-tight">Client Tracker</h1>
            <p className="text-xs sm:text-sm text-[hsl(var(--text-muted))]">{ib?.name} — Access, Renew & Follow-up Management</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {IS_PREVIEW && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline" className="text-xs h-8" data-testid="dev-menu-trigger">
                    <Zap size={13} className="mr-1 text-[#F59E0B]" /> Dev
                    <ChevronDown size={10} className="ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={seedPreview} data-testid="btn-seed"><Sparkles size={13} className="mr-2 text-[#F59E0B]" />Seed Preview Data</DropdownMenuItem>
                  <DropdownMenuItem onClick={clearSeeds} data-testid="btn-clear-seed"><RefreshCcw size={13} className="mr-2 text-[#EF4444]" />Clear Preview Seeds</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <Button size="sm" variant="outline" className="text-xs h-8" onClick={() => setShowCommission(true)} data-testid="btn-commission">
              <DollarSign size={13} className="mr-1 text-[#10B981]" /> Commission
            </Button>
            <Button size="sm" variant="outline" className="text-xs h-8" onClick={exportCSV} data-testid="btn-export">
              <Download size={13} className="mr-1" /> Export CSV
            </Button>
            <Button size="sm" variant="outline" className="text-xs h-8" onClick={() => setShowImport(true)} data-testid="btn-import">
              <Upload size={13} className="mr-1" /> Import
            </Button>
            <Button size="sm" className="text-xs h-8 bg-[#E63946] hover:bg-[#c62828]" onClick={openCreate} data-testid="btn-add-client">
              <Plus size={13} className="mr-1" /> Add Client
            </Button>
          </div>
        </div>
      </header>

      <div className="px-4 sm:px-8 space-y-5">
        {/* Renewal reminder banner */}
        {renewal && renewal.total > 0 && (
          <div
            className={`rounded-xl border px-4 py-3 flex items-center gap-3 flex-wrap ${renewal.counts.expired > 0 ? 'border-[#EF4444]/40 bg-[#EF4444]/10' : 'border-[#F59E0B]/40 bg-[#F59E0B]/10'}`}
            data-testid="renewal-banner"
          >
            <Bell size={15} className={renewal.counts.expired > 0 ? 'text-[#EF4444]' : 'text-[#F59E0B]'} />
            <div className="min-w-0">
              <p className="text-[12px] font-bold text-white" data-testid="renewal-banner-headline">
                {renewal.total} client perlu perhatian —{' '}
                {renewal.counts.expired > 0 && <span className="text-[#EF4444]">{renewal.counts.expired} dah expired</span>}
                {renewal.counts.expired > 0 && (renewal.counts.today + renewal.counts.in_3d + renewal.counts.in_7d) > 0 && ' · '}
                {renewal.counts.today > 0 && <span className="text-[#F59E0B]">{renewal.counts.today} expiry hari ini</span>}
                {renewal.counts.today > 0 && (renewal.counts.in_3d + renewal.counts.in_7d) > 0 && ' · '}
                {renewal.counts.in_3d > 0 && <span className="text-[#F59E0B]">{renewal.counts.in_3d} dalam 3 hari</span>}
                {renewal.counts.in_3d > 0 && renewal.counts.in_7d > 0 && ' · '}
                {renewal.counts.in_7d > 0 && <span className="text-[#10B981]">{renewal.counts.in_7d} dalam 7 hari</span>}
              </p>
              <p className="text-[10px] text-[hsl(var(--text-muted))] truncate">
                {[...(renewal.buckets.expired || []), ...(renewal.buckets.today || []), ...(renewal.buckets.in_3d || []), ...(renewal.buckets.in_7d || [])]
                  .slice(0, 4)
                  .map((c) => `${c.name} (${c.expiry_date})`)
                  .join(' · ')}
                {renewal.total > 4 ? ` · +${renewal.total - 4} lagi` : ''}
              </p>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <Button
                size="sm"
                variant={expiringOnly ? 'default' : 'outline'}
                className={`h-8 text-[10px] ${expiringOnly ? 'bg-[#E63946] hover:bg-[#d32f3f] text-white' : ''}`}
                onClick={() => setExpiringOnly((v) => !v)}
                data-testid="renewal-filter-toggle"
              >
                {expiringOnly ? 'Tunjuk Semua' : 'Tunjuk Yang Ni Saja'}
              </Button>
            </div>
          </div>
        )}

        {/* Score Cards (matches Product Installation) */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <ScoreCard label="Total"    value={agg.total}       icon={Users}       color="#3B82F6" hint={`${(agg.by_client_type?.existing) || 0} existing`} testId="stat-total" />
          <ScoreCard label="Active"   value={agg.active}      icon={TrendingUp}  color="#10B981" hint={`${agg.due_followups || 0} due follow-up`} testId="stat-active" />
          <ScoreCard label="Expiring Soon" value={agg.expiring_soon || 0} icon={AlertTriangle} color="#EAB308" hint="7 hari lagi" testId="stat-expiring-soon" />
          <ScoreCard label="Expired"  value={agg.expired}     icon={AlertTriangle} color="#F59E0B" hint={`${agg.expiring_7d} exp in 7d`} testId="stat-expired" />
          <ScoreCard label="New Client" value={(agg.by_client_type?.new) || 0} icon={UserX} color="#3B82F6" hint={`${(agg.by_client_type?.existing) || 0} existing`} testId="stat-new-client" />
          <ScoreCard label="Repeat / Renew" value={`${agg.repeat_clients || 0}`} icon={Package} color="#8B5CF6" hint={`${agg.total_renewals || 0} renewals`} testId="stat-repeat" />
          <ScoreCard label="Total Deposit" value={fmtMYR(agg.total_deposit_myr)} icon={DollarSign} color="#E63946" hint={`Avg ${fmtMYR(agg.avg_deposit_myr)}`} testId="stat-deposit" />
        </div>

        {/* Expiring Soon Alert */}
        {(expiringSoon.d1.length + expiringSoon.d3.length + expiringSoon.d7.length + agg.due_followups) > 0 && (
          <SectionCard>
            <SectionHeader
              icon={ShieldAlert}
              title="Reminders & Expiring Soon"
              subtitle="Team perlu follow-up client-client ini sebelum expired"
            />
            <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="expiring-panel">
              {[
                { key: 'd1', label: 'Expiring 1 Day', color: '#EF4444', list: expiringSoon.d1 },
                { key: 'd3', label: 'Expiring 3 Days', color: '#F59E0B', list: expiringSoon.d3 },
                { key: 'd7', label: 'Expiring 7 Days', color: '#10B981', list: expiringSoon.d7 },
              ].map((b) => (
                <div key={b.key} className="rounded-lg border p-3" style={{ borderColor: `${b.color}30`, background: `${b.color}08` }} data-testid={`expiry-bucket-${b.key}`}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[10px] uppercase font-bold tracking-wider" style={{ color: b.color }}>{b.label}</p>
                    <span className="text-sm font-black" style={{ color: b.color }}>{b.list.length}</span>
                  </div>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {b.list.length === 0 && <p className="text-[10px] text-[hsl(var(--text-muted))]">Tiada.</p>}
                    {b.list.slice(0, 6).map((r) => (
                      <button key={r.id} onClick={() => openEdit(r)} className="w-full text-left flex items-center justify-between px-2 py-1 rounded hover:bg-[hsl(var(--surface-overlay))] transition-all" data-testid={`expiry-chip-${r.id}`}>
                        <span className="text-[11px] text-white truncate">{r.name}</span>
                        <span className="text-[9px] text-[hsl(var(--text-muted))] shrink-0">{r.daysLeft <= 0 ? 'today' : `${r.daysLeft}d`}</span>
                      </button>
                    ))}
                    {b.list.length > 6 && <p className="text-[9px] text-[hsl(var(--text-muted))]">+{b.list.length - 6} more</p>}
                  </div>
                </div>
              ))}
            </div>
            {agg.due_followups > 0 && (
              <div className="px-4 pb-3 pt-1 text-[11px] text-[hsl(var(--text-muted))]" data-testid="followup-line">
                <Bell size={11} className="inline-block mr-1 text-[#8B5CF6]" />
                <span className="text-[#8B5CF6] font-bold">{agg.due_followups}</span> follow-up{agg.due_followups > 1 ? 's' : ''} due today (Active/Prospect clients).
              </div>
            )}
          </SectionCard>
        )}

        {/* Pipeline — 3 charts (Status / Product / Broker) */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <SectionCard>
            <SectionHeader icon={Users} title="Client Status Pipeline" subtitle="Distribution by status" />
            <div className="p-3 h-[220px]" data-testid="pipeline-status">
              {statusPieData.length === 0 ? (
                <p className="text-center text-[11px] text-[hsl(var(--text-muted))] py-20">Tiada data.</p>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={statusPieData} dataKey="value" nameKey="name" innerRadius={40} outerRadius={70} paddingAngle={2}>
                      {statusPieData.map((d) => <Cell key={d.key} fill={d.color} />)}
                    </Pie>
                    <ReTooltip contentStyle={{ background: '#1a1a25', border: '1px solid hsl(var(--border))', borderRadius: 8, color: '#fff', fontSize: 11 }} />
                    <Legend iconSize={8} wrapperStyle={{ fontSize: 10 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </SectionCard>

          <SectionCard>
            <SectionHeader icon={Package} title="Product Mix" subtitle="Which product they joined for" />
            <div className="p-3 h-[220px]" data-testid="pipeline-product">
              {productPieData.length === 0 ? (
                <p className="text-center text-[11px] text-[hsl(var(--text-muted))] py-20">Tiada data.</p>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={productPieData} dataKey="value" nameKey="name" innerRadius={40} outerRadius={70} paddingAngle={2}>
                      {productPieData.map((d) => <Cell key={d.key} fill={d.color} />)}
                    </Pie>
                    <ReTooltip contentStyle={{ background: '#1a1a25', border: '1px solid hsl(var(--border))', borderRadius: 8, color: '#fff', fontSize: 11 }} />
                    <Legend iconSize={8} wrapperStyle={{ fontSize: 10 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </SectionCard>

          <SectionCard>
            <SectionHeader icon={ShieldAlert} title="Broker Distribution" subtitle="Top brokers by client count" />
            <div className="p-3 h-[220px] overflow-y-auto" data-testid="pipeline-broker">
              {(!agg.by_broker || Object.keys(agg.by_broker).length === 0) ? (
                <p className="text-center text-[11px] text-[hsl(var(--text-muted))] py-20">Tiada data.</p>
              ) : (
                <div className="space-y-2">
                  {Object.entries(agg.by_broker).slice(0, 8).map(([broker, count]) => {
                    const pct = agg.total > 0 ? Math.round((count / agg.total) * 100) : 0;
                    return (
                      <div key={broker} className="flex items-center gap-2" data-testid={`broker-row-${broker}`}>
                        <span className="text-[11px] text-white font-semibold w-24 truncate" title={broker}>{broker}</span>
                        <div className="flex-1 h-4 bg-[hsl(var(--surface-elevated))] rounded overflow-hidden relative">
                          <div className="h-full bg-gradient-to-r from-[#E63946] to-[#F59E0B]" style={{ width: `${pct}%` }} />
                          <span className="absolute inset-0 flex items-center justify-end pr-2 text-[9px] text-white font-mono font-bold">{count}</span>
                        </div>
                        <span className="text-[10px] text-[hsl(var(--text-muted))] font-mono w-9 text-right">{pct}%</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </SectionCard>
        </div>

        {/* Closed-by leaderboard (small strip) */}
        {agg.by_closed_by && Object.keys(agg.by_closed_by).filter((k) => k !== '—').length > 0 && (
          <SectionCard>
            <SectionHeader icon={UserPlus} title="Closed By — Sales Leaderboard" subtitle="Client count per closer" />
            <div className="p-3 flex flex-wrap gap-2" data-testid="closed-by-leaderboard">
              {Object.entries(agg.by_closed_by).filter(([k]) => k && k !== '—').map(([who, count]) => (
                <div key={who} className="px-3 py-1.5 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--surface-elevated))] flex items-center gap-2 text-[11px]" data-testid={`closed-by-${who}`}>
                  <span className="text-white font-semibold">{who}</span>
                  <span className="text-[#E63946] font-mono font-bold">{count}</span>
                </div>
              ))}
            </div>
          </SectionCard>
        )}

        {/* Filters row */}
        <SectionCard>
          <div className="px-4 py-3 flex items-center gap-2 overflow-x-auto no-scrollbar" data-testid="client-filter-bar">
            <AutocompleteSearch
              ibId={ibId}
              value={search}
              onChange={setSearch}
              onPick={(s) => { const row = rows.find((r) => r.id === s.id); if (row) openEdit(row); else setSearch(s.name); }}
            />
            {monthOptions.length > 0 && (
              <Select value={monthFilter} onValueChange={setMonthFilter}>
                <SelectTrigger className="w-[112px] shrink-0 h-8 text-[10px] bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]" data-testid="month-filter">
                  <SelectValue placeholder="Select Month" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Months</SelectItem>
                  {monthOptions.map((m) => <SelectItem key={m} value={m}>{format(new Date(m + '-01'), 'MMM yyyy')}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[126px] shrink-0 h-8 text-[10px] bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]" data-testid="client-status-filter">
                <Filter size={11} className="mr-1" /><SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Access Status</SelectItem>
                {Object.entries(STATUS_META).map(([k, meta]) => <SelectItem key={k} value={k}>{meta.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[118px] shrink-0 h-8 text-[10px] bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]" data-testid="client-type-filter">
                <Users size={11} className="mr-1" /><SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Client Type</SelectItem>
                {Object.entries(CLIENT_TYPE_META).map(([k, meta]) => <SelectItem key={k} value={k}>{meta.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={productFilter} onValueChange={setProductFilter}>
              <SelectTrigger className="w-[112px] shrink-0 h-8 text-[10px] bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]" data-testid="client-product-filter">
                <Package size={11} className="mr-1" /><SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Products</SelectItem>
                {Object.keys(PRODUCT_META).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
            {/* Follow-up filter */}
            <Select value={followUpFilter} onValueChange={setFollowUpFilter}>
              <SelectTrigger className="w-[126px] shrink-0 h-8 text-[10px] bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]" data-testid="client-followup-filter">
                <RefreshCcw size={11} className="mr-1" /><SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Follow-up</SelectItem>
                <SelectItem value="0">Belum Follow Up</SelectItem>
                <SelectItem value="1">Tier 1</SelectItem>
                <SelectItem value="2">Tier 2</SelectItem>
                <SelectItem value="3">Tier 3</SelectItem>
                <SelectItem value="4">Done</SelectItem>
                <SelectItem value="5">Not Interested</SelectItem>
              </SelectContent>
            </Select>
            {/* Date Added — compact popover (single day or range) */}
            <Popover open={dateOpen} onOpenChange={setDateOpen}>
              <PopoverTrigger asChild>
                <button
                  className={`flex items-center gap-1.5 h-8 px-2.5 shrink-0 rounded-md border text-[10px] transition-colors ${
                    dateFrom || dateTo
                      ? 'border-[#E63946] bg-[#E63946]/15 text-white'
                      : 'border-[hsl(var(--border))] bg-[hsl(var(--surface-elevated))] text-[hsl(var(--text-muted))] hover:text-white'
                  }`}
                  data-testid="date-added-trigger"
                >
                  <Clock size={11} />
                  <span className="font-semibold uppercase tracking-wider">
                    {dateFrom || dateTo
                      ? (dateFrom === dateTo ? dateFrom : `${dateFrom || '…'} → ${dateTo || '…'}`)
                      : 'Date Added'}
                  </span>
                  <ChevronDown size={10} />
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-64 p-3 space-y-2 bg-[hsl(var(--card))] border-[hsl(var(--border))]" data-testid="date-added-popover">
                <p className="text-[10px] uppercase font-bold tracking-wider text-[hsl(var(--text-muted))]">Date Added</p>
                <div className="flex items-center gap-1.5">
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => { const v = e.target.value; setDateFrom(v); if (v && !dateTo) setDateTo(v); }}
                    className="flex-1 bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded px-2 py-1 text-[10px] text-white outline-none"
                    data-testid="date-added-from"
                  />
                  <span className="text-[10px] text-[hsl(var(--text-muted))]">→</span>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="flex-1 bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded px-2 py-1 text-[10px] text-white outline-none"
                    data-testid="date-added-to"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-[hsl(var(--text-muted))]" data-testid="date-filter-label">
                    {(dateFrom || dateTo) ? `${rows.length} client` : 'Semua tarikh'}
                  </span>
                  {(dateFrom || dateTo) && (
                    <button
                      onClick={() => { setDateFrom(''); setDateTo(''); }}
                      className="flex items-center gap-1 text-[10px] text-[#E63946] hover:underline"
                      data-testid="date-added-clear"
                    >
                      <X size={10} /> Clear
                    </button>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </SectionCard>

        {/* Client Records — Table ⇄ Follow-up Board */}
        <SectionCard>
          <SectionHeader
            icon={Users}
            title={view === 'board' ? 'Follow-up Board' : view === 'priority' ? 'Priority Follow-up' : 'Client Records'}
            subtitle={`${visibleRows.length} record${visibleRows.length !== 1 ? 's' : ''}`}
            right={(
              <div className="flex items-center gap-3">
                <div className="inline-flex bg-[hsl(var(--surface-elevated))] rounded-md p-0.5 border border-[hsl(var(--border))]" data-testid="client-view-toggle">
                  {[['table', 'Table'], ['board', 'Board'], ['priority', 'Priority']].map(([k, label]) => (
                    <button
                      key={k}
                      onClick={() => setView(k)}
                      className={`px-2.5 py-1 rounded text-[10px] font-semibold transition-colors ${view === k ? 'bg-[#E63946] text-white' : 'text-[hsl(var(--text-muted))] hover:text-white'}`}
                      data-testid={`view-${k}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                {view === 'table' && visibleRows.length > 0 && (
                  <div className="flex items-center gap-2 text-[10px] text-[hsl(var(--text-muted))]">
                    <span>Per page:</span>
                    <select
                      value={pageSize}
                      onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                      className="bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded px-2 py-1 text-white text-[10px] outline-none focus:border-[#E63946]"
                      data-testid="client-page-size"
                    >
                      <option value={15}>15</option>
                      <option value={30}>30</option>
                      <option value={50}>50</option>
                    </select>
                  </div>
                )}
              </div>
            )}
          />
          {view === 'priority' ? (
            <ClientPriorityList ibId={ibId} todayISO={todayISO} onChanged={fetchList} />
          ) : view === 'board' ? (
            <div className="p-3">
              {loading ? (
                <div className="p-12 text-center"><Loader2 size={20} className="animate-spin text-[#E63946] mx-auto" /></div>
              ) : (
                <ClientFollowUpBoard rows={visibleRows} ibId={ibId} todayISO={todayISO} onChanged={fetchList} />
              )}
            </div>
          ) : (
          <div className="overflow-x-auto">
            {loading ? (
              <div className="p-12 text-center"><Loader2 size={20} className="animate-spin text-[#E63946] mx-auto" /></div>
            ) : visibleRows.length === 0 ? (
              <div className="p-12 text-center text-xs text-[hsl(var(--text-muted))]">
                Belum ada client. Klik <span className="text-white">Add Client</span> atau <span className="text-white">Import</span> untuk mula.
              </div>
            ) : (
              <table className="w-full text-[11px]" data-testid="client-table">
                <thead>
                  <tr className="border-b border-[hsl(var(--border))]">
                    <th className="py-2.5 px-3 text-left text-[9px] font-bold text-[hsl(var(--text-muted))] uppercase tracking-wider">Name</th>
                    <th className="py-2.5 px-3 text-left text-[9px] font-bold text-[hsl(var(--text-muted))] uppercase tracking-wider">Account ID</th>
                    <th className="py-2.5 px-3 text-left text-[9px] font-bold text-[hsl(var(--text-muted))] uppercase tracking-wider">Client Type</th>
                    <th className="py-2.5 px-3 text-left text-[9px] font-bold text-[hsl(var(--text-muted))] uppercase tracking-wider">Access Status</th>
                    <th className="py-2.5 px-3 text-center text-[9px] font-bold text-[hsl(var(--text-muted))] uppercase tracking-wider">Renew</th>
                    <th className="py-2.5 px-3 text-right text-[9px] font-bold text-[hsl(var(--text-muted))] uppercase tracking-wider">Deposit</th>
                    <th className="py-2.5 px-3 text-left text-[9px] font-bold text-[hsl(var(--text-muted))] uppercase tracking-wider">Closed By</th>
                    <th className="py-2.5 px-3 text-left text-[9px] font-bold text-[hsl(var(--text-muted))] uppercase tracking-wider">Date Added</th>
                    <th className="py-2.5 px-3 text-left text-[9px] font-bold text-[hsl(var(--text-muted))] uppercase tracking-wider">Date Expiry</th>
                    <th className="py-2.5 px-3 text-left text-[9px] font-bold text-[hsl(var(--text-muted))] uppercase tracking-wider">Follow-up</th>
                    <th className="py-2.5 px-3 text-left text-[9px] font-bold text-[hsl(var(--text-muted))] uppercase tracking-wider">Follow-up Date</th>
                    <th className="py-2.5 px-3 text-left text-[9px] font-bold text-[hsl(var(--text-muted))] uppercase tracking-wider">Next Follow-up</th>
                    <th className="py-2.5 px-3 text-left text-[9px] font-bold text-[hsl(var(--text-muted))] uppercase tracking-wider">Follow Up By</th>
                    <th className="py-2.5 px-3 text-left text-[9px] font-bold text-[hsl(var(--text-muted))] uppercase tracking-wider max-w-[180px]">Remarks</th>
                    <th className="py-2.5 px-3 text-left text-[9px] font-bold text-[hsl(var(--text-muted))] uppercase tracking-wider">Edited By</th>
                    <th className="py-2.5 px-3 text-right text-[9px] font-bold text-[hsl(var(--text-muted))] uppercase tracking-wider w-24">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows
                    .slice((currentPage - 1) * pageSize, currentPage * pageSize)
                    .map((r) => {
                    const state = r.access_state || accessState(r.expiry_date, todayISO)
                      || (r.status === 'expired' ? 'expired' : 'active');
                    const meta = STATUS_META[state] || STATUS_META.expired;
                    const daysToExpiry = r.expiry_date
                      ? Math.floor((new Date(r.expiry_date) - new Date(todayISO)) / (1000 * 60 * 60 * 24))
                      : null;
                    const expiryColor = daysToExpiry === null ? 'text-[hsl(var(--text-muted))]'
                      : daysToExpiry < 0 ? 'text-[#EF4444] font-bold'
                      : daysToExpiry <= 3 ? 'text-[#F59E0B] font-bold'
                      : daysToExpiry <= 7 ? 'text-[#10B981]'
                      : 'text-white';
                    const overdue = r.next_follow_up_date && r.next_follow_up_date <= todayISO && r.status === 'active';
                    const typeMeta = CLIENT_TYPE_META[(r.client_type || 'new').toLowerCase()] || CLIENT_TYPE_META.new;
                    const tier = Number(r.follow_up_tier || 0);
                    const daysSinceFU = r.follow_up_updated_at
                      ? Math.round((new Date(todayISO) - new Date(r.follow_up_updated_at)) / 86400000)
                      : null;
                    const fuDue = !CLOSED_TIERS.includes(tier) && daysSinceFU !== null
                      && daysSinceFU >= (TIER_DUE_DAYS[tier] ?? 0);
                    const renew = Number(r.renew_count || 1);
                    // Next contact date follows the tier cadence (Belum = today,
                    // Tier 1 +1d, Tier 2 +3d, Tier 3 +7d, closed = none)
                    const nextFU = CLOSED_TIERS.includes(tier)
                      ? ''
                      : (r.next_follow_up_date
                        || (tier === 0 || !r.follow_up_updated_at
                          ? todayISO
                          : new Date(new Date(r.follow_up_updated_at).getTime() + (TIER_DUE_DAYS[tier] ?? 0) * 86400000)
                            .toISOString().slice(0, 10)));
                    return (
                      <tr key={r.id} className="border-b border-[hsl(var(--border))]/40 hover:bg-[hsl(var(--surface-elevated))] transition-colors" data-testid={`client-row-${r.id}`}>
                        <td className="py-2.5 px-3">
                          <HoverCard openDelay={150}>
                            <HoverCardTrigger asChild>
                              <button
                                onClick={() => openDetails(r)}
                                className="font-semibold text-white text-left hover:text-[#E63946] transition-colors underline decoration-dotted decoration-[hsl(var(--border))] underline-offset-4"
                                data-testid={`client-name-${r.id}`}
                              >
                                {r.name}
                              </button>
                            </HoverCardTrigger>
                            <HoverCardContent className="w-72 bg-[#0a0a0f] border-[#1a1a25] text-white" data-testid={`client-hover-${r.id}`}>
                              <p className="text-[12px] font-bold mb-1.5">{r.name}</p>
                              <div className="space-y-1 text-[10px] text-[hsl(var(--text-muted))]">
                                {r.email && <div className="flex items-center gap-1.5"><Mail size={10} /> {r.email}</div>}
                                {r.phone && <div className="flex items-center gap-1.5"><Phone size={10} /> {r.phone}</div>}
                                <div className="flex items-center gap-1.5"><Package size={10} /> {r.product || 'Others'}</div>
                                <div className="flex items-center gap-1.5"><ShieldAlert size={10} /> {r.broker || '—'}</div>
                                <div className="flex items-center gap-1.5"><DollarSign size={10} /> {fmtMYR(r.deposit_myr)} deposit</div>
                              </div>
                              <p className="text-[9px] text-[hsl(var(--text-muted))] mt-2 italic">Klik nama untuk full details + history</p>
                            </HoverCardContent>
                          </HoverCard>
                        </td>
                        <td className="py-2.5 px-3 text-white font-mono text-[10px]">{r.account_id || '—'}</td>
                        <td className="py-2.5 px-3">
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded border ${typeMeta.bg}`} data-testid={`client-type-${r.id}`}>{typeMeta.label}</span>
                        </td>
                        <td className="py-2.5 px-3">
                          <span
                            className={`text-[10px] font-semibold px-2 py-1 rounded border ${meta.bg}`}
                            data-testid={`status-badge-${r.id}`}
                            title={`Auto ikut Date Expiry (${r.expiry_date || 'tiada'})`}
                          >
                            {meta.label}
                          </span>
                        </td>
                        <td className="py-2.5 px-3 text-center" data-testid={`renew-count-${r.id}`}>
                          <span className={`text-[11px] font-black font-mono ${renew >= 2 ? 'text-[#10B981]' : 'text-white'}`}>{renew}x</span>
                        </td>
                        <td className="py-2.5 px-3 text-right text-white font-mono">{fmtMYR(r.deposit_myr)}</td>
                        <td className="py-2.5 px-3 text-[hsl(var(--text-muted))] text-[10px]">{r.closed_by || '—'}</td>
                        <td className="py-2.5 px-3 text-[10px] text-white">{r.joined_date || '—'}</td>
                        <td className={`py-2.5 px-3 text-[10px] ${expiryColor}`}>
                          {r.expiry_date || '—'}
                          {daysToExpiry !== null && daysToExpiry >= 0 && daysToExpiry <= 7 && (
                            <span className="ml-1 text-[9px]">({daysToExpiry}d)</span>
                          )}
                        </td>
                        <td className="py-2.5 px-3">
                          <select
                            value={tier}
                            onChange={(e) => updateTierInline(r, e.target.value)}
                            style={{ color: TIER_META[tier].color, borderColor: `${TIER_META[tier].color}55` }}
                            className="text-[10px] font-semibold rounded px-2 py-1 outline-none cursor-pointer bg-[hsl(var(--surface-elevated))] border hover:brightness-125 transition-all"
                            data-testid={`tier-select-${r.id}`}
                            title="Berapa kali dah follow up"
                          >
                            {Object.entries(TIER_META).map(([k, m]) => (
                              <option key={k} value={k} style={{ background: '#111827', color: m.color }}>
                                {k === '0' ? 'Belum' : m.label}
                              </option>
                            ))}
                          </select>
                          {overdue && <AlertTriangle size={10} className="inline-block ml-1 text-[#F59E0B]" title="Follow-up overdue" />}
                        </td>
                        <td className="py-2.5 px-3 text-[10px]" data-testid={`follow-up-date-${r.id}`}>
                          {r.follow_up_updated_at ? (
                            <>
                              <span className="text-white">{r.follow_up_updated_at}</span>
                              {daysSinceFU !== null && (
                                <span className="ml-1 text-[9px] text-[hsl(var(--text-muted))]">({daysSinceFU}d)</span>
                              )}
                              {fuDue && (
                                <span className="ml-1 text-[9px] font-bold text-[#EF4444]" data-testid={`fu-due-${r.id}`}>DUE</span>
                              )}
                            </>
                          ) : (
                            <span className="text-[hsl(var(--text-muted))]">—</span>
                          )}
                        </td>
                        <td className="py-2.5 px-3 text-[10px]" data-testid={`next-follow-up-${r.id}`}>
                          {nextFU ? (
                            <>
                              <span className={nextFU <= todayISO ? 'text-[#EF4444] font-bold' : 'text-white'}>{nextFU}</span>
                              {nextFU <= todayISO && (
                                <span className="ml-1 text-[9px] font-bold text-[#EF4444]">DUE</span>
                              )}
                            </>
                          ) : (
                            <span className="text-[hsl(var(--text-muted))]">—</span>
                          )}
                        </td>
                        <td className="py-2.5 px-3">
                          <select
                            value={r.follow_up_by || ''}
                            onChange={(e) => updateFollowUpBy(r, e.target.value)}
                            className="text-[10px] rounded px-2 py-1 outline-none cursor-pointer bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] text-white hover:border-[hsl(var(--text-muted))] transition-colors"
                            data-testid={`follow-up-by-${r.id}`}
                            title="Admin sales yang follow up"
                          >
                            <option value="" style={{ background: '#111827' }}>—</option>
                            {[...new Set([...SALES_ADMINS, ...(r.follow_up_by ? [r.follow_up_by] : [])])].map((a) => (
                              <option key={a} value={a} style={{ background: '#111827' }}>{a}</option>
                            ))}
                          </select>
                        </td>
                        <td className="py-2.5 px-3 text-[10px] text-[hsl(var(--text-muted))] max-w-[180px]">
                          <div className="truncate" title={r.remarks || ''}>{r.remarks || '—'}</div>
                        </td>
                        <td className="py-2.5 px-3 text-[10px] text-[hsl(var(--text-muted))]" data-testid={`edited-by-${r.id}`}>
                          {r.last_edited_by ? (
                            <span title={r.last_edited_at || ''}>{r.last_edited_by}</span>
                          ) : '—'}
                        </td>
                        <td className="py-2.5 px-3 text-right">
                          <div className="flex justify-end gap-1">
                            <button onClick={() => openEdit(r)} className="p-1 rounded hover:bg-[hsl(var(--surface-overlay))] text-[hsl(var(--text-muted))] hover:text-white" title="Edit" data-testid={`btn-edit-${r.id}`}>
                              <Edit3 size={12} />
                            </button>
                            <button onClick={() => remove(r)} className="p-1 rounded hover:bg-[#EF4444]/10 text-[hsl(var(--text-muted))] hover:text-[#EF4444]" title="Delete" data-testid={`btn-delete-${r.id}`}>
                              <Trash2 size={12} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          )}
          {/* Pagination footer */}
          {view === 'table' && visibleRows.length > pageSize && (
            <div className="px-4 py-3 border-t border-[hsl(var(--border))] flex items-center justify-between gap-3 flex-wrap">
              <div className="text-[10px] text-[hsl(var(--text-muted))]" data-testid="client-pagination-info">
                Showing <span className="text-white font-bold">{(currentPage - 1) * pageSize + 1}</span>–<span className="text-white font-bold">{Math.min(currentPage * pageSize, visibleRows.length)}</span> of <span className="text-white font-bold">{visibleRows.length}</span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="px-2 py-1 rounded text-[10px] font-semibold bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] text-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[hsl(var(--surface-overlay))]"
                  data-testid="page-first"
                >« First</button>
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-2 py-1 rounded text-[10px] font-semibold bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] text-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[hsl(var(--surface-overlay))]"
                  data-testid="page-prev"
                >‹ Prev</button>
                <span className="px-3 py-1 text-[10px] font-bold text-white" data-testid="page-current">
                  {currentPage} / {Math.max(1, Math.ceil(visibleRows.length / pageSize))}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(Math.ceil(visibleRows.length / pageSize), p + 1))}
                  disabled={currentPage >= Math.ceil(visibleRows.length / pageSize)}
                  className="px-2 py-1 rounded text-[10px] font-semibold bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] text-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[hsl(var(--surface-overlay))]"
                  data-testid="page-next"
                >Next ›</button>
                <button
                  onClick={() => setCurrentPage(Math.ceil(visibleRows.length / pageSize))}
                  disabled={currentPage >= Math.ceil(visibleRows.length / pageSize)}
                  className="px-2 py-1 rounded text-[10px] font-semibold bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] text-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[hsl(var(--surface-overlay))]"
                  data-testid="page-last"
                >Last »</button>
              </div>
            </div>
          )}
        </SectionCard>
      </div>

      {/* Add / Edit dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white max-h-[90vh] overflow-y-auto" data-testid="client-form-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Client' : 'Add New Client'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="text-[10px] uppercase text-[hsl(var(--text-muted))] font-bold">Name *</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white" data-testid="input-name" />
            </div>
            <div>
              <label className="text-[10px] uppercase text-[hsl(var(--text-muted))] font-bold">Email</label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white" data-testid="input-email" />
            </div>
            <div>
              <label className="text-[10px] uppercase text-[hsl(var(--text-muted))] font-bold">Phone</label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white" data-testid="input-phone" />
            </div>
            <div>
              <label className="text-[10px] uppercase text-[hsl(var(--text-muted))] font-bold">Broker</label>
              <Input value={form.broker} onChange={(e) => setForm({ ...form, broker: e.target.value })} className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white" data-testid="input-broker" />
            </div>
            <div>
              <label className="text-[10px] uppercase text-[hsl(var(--text-muted))] font-bold">Account ID</label>
              <Input value={form.account_id} onChange={(e) => setForm({ ...form, account_id: e.target.value })} className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white" data-testid="input-account-id" placeholder="e.g. XM-402193" />
            </div>
            <div>
              <label className="text-[10px] uppercase text-[hsl(var(--text-muted))] font-bold">Product</label>
              <Select value={form.product} onValueChange={(v) => setForm({ ...form, product: v })}>
                <SelectTrigger className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white" data-testid="input-product"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.keys(PRODUCT_META).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase text-[hsl(var(--text-muted))] font-bold">Client Type</label>
              <Select value={form.client_type || 'new'} onValueChange={(v) => setForm({ ...form, client_type: v })}>
                <SelectTrigger className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white" data-testid="input-client-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(CLIENT_TYPE_META).map(([k, meta]) => <SelectItem key={k} value={k}>{meta.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase text-[hsl(var(--text-muted))] font-bold">Access Status</label>
              <div className="h-10 flex items-center" data-testid="input-status">
                {(() => {
                  const st = accessState(form.expiry_date, todayISO) || 'active';
                  const m = STATUS_META[st];
                  return (
                    <span className={`text-[11px] font-semibold px-2.5 py-1 rounded border ${m.bg}`}>
                      {m.label} <span className="opacity-60">· auto ikut Date Expiry</span>
                    </span>
                  );
                })()}
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase text-[hsl(var(--text-muted))] font-bold">Follow-up Tier</label>
              <Select value={String(form.follow_up_tier ?? 0)} onValueChange={(v) => setForm({ ...form, follow_up_tier: Number(v) })}>
                <SelectTrigger className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white" data-testid="input-follow-up-tier"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TIER_META).map(([k, m]) => <SelectItem key={k} value={k}>{k === '0' ? 'Belum follow up' : m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase text-[hsl(var(--text-muted))] font-bold">Follow Up By</label>
              <Select value={form.follow_up_by || '__none__'} onValueChange={(v) => setForm({ ...form, follow_up_by: v === '__none__' ? '' : v })}>
                <SelectTrigger className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white" data-testid="input-follow-up-by"><SelectValue placeholder="Pilih admin sales" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— None —</SelectItem>
                  {[...new Set([...SALES_ADMINS, ...(form.follow_up_by ? [form.follow_up_by] : [])])].map((a) => (
                    <SelectItem key={a} value={a}>{a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase text-[hsl(var(--text-muted))] font-bold">Renew / Repeat (x)</label>
              <Input type="number" min={0} value={form.renew_count ?? 1} onChange={(e) => setForm({ ...form, renew_count: Number(e.target.value) })} className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white" data-testid="input-renew-count" />
              <p className="text-[9px] text-[hsl(var(--text-muted))] mt-0.5">Naik automatik bila Expiry Date ditolak ke depan</p>
            </div>
            <div>
              <label className="text-[10px] uppercase text-[hsl(var(--text-muted))] font-bold">Closed By (Sales)</label>
              <Select value={form.closed_by || '__none__'} onValueChange={(v) => setForm({ ...form, closed_by: v === '__none__' ? '' : v })}>
                <SelectTrigger className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white" data-testid="input-closed-by"><SelectValue placeholder="Pilih admin sales" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— None —</SelectItem>
                  {[...new Set([...SALES_ADMINS, ...(form.closed_by ? [form.closed_by] : [])])].map((a) => (
                    <SelectItem key={a} value={a}>{a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase text-[hsl(var(--text-muted))] font-bold">Deposit (MYR)</label>
              <Input type="number" value={form.deposit_myr} onChange={(e) => setForm({ ...form, deposit_myr: e.target.value })} className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white" data-testid="input-deposit" />
            </div>
            <div>
              <label className="text-[10px] uppercase text-[hsl(var(--text-muted))] font-bold">Joined Date</label>
              <Input type="date" value={form.joined_date} onChange={(e) => setForm({ ...form, joined_date: e.target.value })} className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white" data-testid="input-joined" />
            </div>
            <div>
              <label className="text-[10px] uppercase text-[hsl(var(--text-muted))] font-bold">Expiry Date</label>
              <Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white" data-testid="input-expiry" />
            </div>
            <div>
              <label className="text-[10px] uppercase text-[hsl(var(--text-muted))] font-bold">Last Contact</label>
              <Input type="date" value={form.last_contact_date} onChange={(e) => setForm({ ...form, last_contact_date: e.target.value })} className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white" data-testid="input-lastcontact" />
            </div>
            <div>
              <label className="text-[10px] uppercase text-[hsl(var(--text-muted))] font-bold">Next Follow-up</label>
              <Input type="date" value={form.next_follow_up_date} onChange={(e) => setForm({ ...form, next_follow_up_date: e.target.value })} className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white" data-testid="input-followup" />
            </div>
            <div className="col-span-2">
              <label className="text-[10px] uppercase text-[hsl(var(--text-muted))] font-bold">Remarks</label>
              <Input value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white" data-testid="input-remarks" placeholder="Short remark shown in table" />
            </div>
            <div className="col-span-2">
              <label className="text-[10px] uppercase text-[hsl(var(--text-muted))] font-bold">Notes (Internal)</label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white" rows={3} data-testid="input-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowForm(false)} className="text-[hsl(var(--text-muted))]">Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-[#E63946] hover:bg-[#d32f3f] text-white" data-testid="btn-save-client">
              {saving ? <Loader2 size={13} className="animate-spin mr-1.5" /> : null}
              {editingId ? 'Save Changes' : 'Add Client'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import dialog */}
      <Dialog open={showImport} onOpenChange={setShowImport}>
        <DialogContent className="max-w-md bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white" data-testid="import-dialog">
          <DialogHeader>
            <DialogTitle>Import Clients</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-[11px] text-[hsl(var(--text-muted))]">
              Upload CSV or JSON file. CSV header row required. Supported columns: <span className="text-white">name, email, phone, broker, account_id, product, client_type, status, follow_up_tier, renew_count, closed_by, deposit_myr, joined_date, expiry_date, next_follow_up_date, remarks, notes</span>.
            </p>
            <div className="rounded-lg border-2 border-dashed border-[hsl(var(--border))] p-6 text-center hover:border-[#E63946]/50 transition-all cursor-pointer" onClick={() => importInputRef.current?.click()} data-testid="import-drop">
              <FileSpreadsheet size={20} className="mx-auto text-[hsl(var(--text-muted))] mb-2" />
              <p className="text-[12px] text-white">Click to choose CSV / JSON file</p>
              <p className="text-[10px] text-[hsl(var(--text-muted))] mt-1">Max 5000 rows</p>
              <input ref={importInputRef} type="file" accept=".csv,.json" onChange={handleImport} className="hidden" data-testid="import-input" />
            </div>
            <div className="text-[10px] text-[hsl(var(--text-muted))]">
              <span className="text-white font-bold">Tip:</span> Export existing clients first to see the exact column format.
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowImport(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Client details + edit history */}
      <Dialog open={!!detailRow} onOpenChange={(v) => { if (!v) { setDetailRow(null); setDetailHistory([]); } }}>
        <DialogContent className="max-w-2xl bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white max-h-[90vh] overflow-y-auto" data-testid="client-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm">{detailRow?.name}</DialogTitle>
          </DialogHeader>
          {detailRow && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  ['Email', detailRow.email],
                  ['Phone', detailRow.phone],
                  ['Product', detailRow.product],
                  ['Broker', detailRow.broker],
                  ['Account ID', detailRow.account_id],
                  ['Client Type', (CLIENT_TYPE_META[(detailRow.client_type || 'new')] || CLIENT_TYPE_META.new).label],
                  ['Access Status', (STATUS_META[detailRow.access_state || accessState(detailRow.expiry_date, todayISO) || detailRow.status] || STATUS_META.expired).label],
                  ['Renew / Repeat', `${detailRow.renew_count || 1}x`],
                  ['Deposit', fmtMYR(detailRow.deposit_myr)],
                  ['Closed By', detailRow.closed_by],
                  ['Date Added', detailRow.joined_date],
                  ['Date Expiry', detailRow.expiry_date],
                  ['Follow-up', (TIER_META[Number(detailRow.follow_up_tier || 0)] || TIER_META[0]).label],
                  ['Follow Up By', detailRow.follow_up_by],
                  ['Last Contact', detailRow.last_contact_date],
                  ['Next Follow-up', detailRow.next_follow_up_date],
                ].map(([k, v]) => (
                  <div key={k} className="bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-lg px-3 py-2">
                    <p className="text-[9px] uppercase font-bold text-[hsl(var(--text-muted))] tracking-wider">{k}</p>
                    <p className="text-[11px] text-white break-words">{v || '—'}</p>
                  </div>
                ))}
              </div>
              {(detailRow.remarks || detailRow.notes) && (
                <div className="bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-lg px-3 py-2 space-y-1">
                  {detailRow.remarks && <p className="text-[11px] text-white"><span className="text-[hsl(var(--text-muted))]">Remarks: </span>{detailRow.remarks}</p>}
                  {detailRow.notes && <p className="text-[11px] text-white"><span className="text-[hsl(var(--text-muted))]">Notes: </span>{detailRow.notes}</p>}
                </div>
              )}
              <div>
                <p className="text-[10px] uppercase font-bold text-[hsl(var(--text-muted))] tracking-wider mb-2">
                  Edit History {detailRow.last_edited_by ? `· last by ${detailRow.last_edited_by}` : ''}
                </p>
                <div className="space-y-2 max-h-56 overflow-y-auto" data-testid="client-edit-history">
                  {detailHistory.length === 0 && (
                    <p className="text-[11px] text-[hsl(var(--text-muted))] italic">Belum ada rekod edit.</p>
                  )}
                  {detailHistory.map((h, i) => (
                    <div key={i} className="border-l-2 border-[#E63946]/50 pl-3 py-1">
                      <p className="text-[10px] text-white font-semibold">
                        {h.by_name} <span className="text-[hsl(var(--text-muted))] font-normal">· {String(h.at || '').replace('T', ' ').slice(0, 16)}</span>
                      </p>
                      {h.note && <p className="text-[9px] text-[#F59E0B]">{h.note}</p>}
                      {(h.changes || []).map((c, j) => (
                        <p key={j} className="text-[9px] text-[hsl(var(--text-muted))]">
                          {c.field}: <span className="text-[#EF4444]">{String(c.from ?? '—')}</span> → <span className="text-[#10B981]">{String(c.to ?? '—')}</span>
                        </p>
                      ))}
                      {(!h.changes || h.changes.length === 0) && !h.note && (
                        <p className="text-[9px] text-[hsl(var(--text-muted))]">—</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <DialogFooter>
                <Button size="sm" variant="outline" className="text-[11px]" onClick={() => { const r = detailRow; setDetailRow(null); openEdit(r); }} data-testid="detail-edit-btn">
                  <Edit3 size={12} className="mr-1" /> Edit Client
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <CommissionBoardModal open={showCommission} onOpenChange={setShowCommission} ibId={ibId} ibName={ib?.name} />
    </div>
  );
}
